import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth, Roles } from "../../providers/AuthContext";
import { getTokenByPublicId, updateToken, resetAccumulatedEnergy } from "../../api/Tokens";
import { getAllInternalCustomers } from "../../api/Customers";
import LoadingSpinner from "../../components/LoadingSpinner";

import toast from "react-hot-toast";
import TokenForm from "../../components/token/TokenForm";
import ConfirmationModal from "../../components/ConfirmationModal";
import { isMaxEnergyKwhInvalid, normalizeMaxEnergyKwh } from "../../helpers/TokenValidity";
import { useTranslation } from "react-i18next";

const EditTokenPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const location = useLocation();
    const { customerPublicId } = location.state || {};

    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
    const [originalToken, setOriginalToken] = useState(null);
    const [resetEnergyPending, setResetEnergyPending] = useState(false);

    const [token, setToken] = useState({
        tagToken: "",
        tokenType: "",
        name: "",
        customerPublicId: "",
        expiredDateTimeUtc: null,
        idTagPublicId: "",
        authorizationStatus: "",
        isActive: true,
        updatedDateTimeUtc: "",
        blockReason: "",
        syncVersion: 0,
        notes: "",
        externalReference: "",
        revocationDateTimeUtc: "",
        version: 0,
        lastUpdatedBy: "",
        tokenVehicleInfo: {}, 
    });

    const handleInputChange = (key, value) => {
        if (key === "tokenVehicleInfo") {
            setToken(prev => ({
                ...prev,
                tokenVehicleInfo: {
                    ...(prev.tokenVehicleInfo || {}),
                    ...value
                }
            }));
        } else {
            setToken(prev => ({ ...prev, [key]: value }));
        }
    };



    // ---------- LOAD DATA ----------
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([handleGetToken(), handleGetCustomers()]);
            setLoading(false);
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [id]);

    
    const handleGetCustomers = async () => {
        try {
            const accessToken = await getAccessToken();
            const newCustomers = await getAllInternalCustomers(accessToken);
            setCustomers(newCustomers);
        } catch (error) {
            console.log(error);
        }
    };

    const handleGetToken = async () => {
        try {
            const accessToken = await getAccessToken();
            const newToken = await getTokenByPublicId(id, accessToken);
            setOriginalToken(newToken);
            setToken(newToken);
            setHasChanges(false);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        setHasChanges(hasTokenChanges(token, originalToken));
    }, [token, originalToken]);

    const hasTokenChanges = (tokenA = {}, tokenB = {}) => {
        // Om nÃƒÂ¥gon ÃƒÂ¤r null/undefined, sÃƒÂ¤tt till tomt objekt
        tokenA = tokenA || {};
        tokenB = tokenB || {};

        // Kolla ÃƒÂ¤ndringar i vanliga fÃƒÂ¤lt
        for (let key of Object.keys(tokenA)) {
            if (key === "tokenVehicleInfo") {
                const vA = tokenA.tokenVehicleInfo || {};
                const vB = tokenB.tokenVehicleInfo || {};
                // kolla keys i vA
                for (let vKey of Object.keys(vA)) {
                    if (vA[vKey] !== vB[vKey]) return true;
                }
                // kolla om vB har extra keys
                for (let vKey of Object.keys(vB)) {
                    if (!(vKey in vA)) return true;
                }
            } else {
                if (tokenA[key] !== tokenB[key]) return true;
            }
        }

        // kolla om tokenB har extra keys
        for (let key of Object.keys(tokenB)) {
            if (!(key in tokenA)) return true;
        }

        return false;
    };

    const normalizeVehicleInfoForApi = (vehicleInfo = {}) => ({
        vehicleBrand: vehicleInfo?.vehicleBrand ?? "",
        vehicleModel: vehicleInfo?.vehicleModel ?? "",
        vehicleType: vehicleInfo?.vehicleType ?? "",
        registrationNumber: vehicleInfo?.registrationNumber ?? "",
        driverName: vehicleInfo?.driverName ?? "",
        driverPhoneNumber: vehicleInfo?.driverPhoneNumber ?? "",
    });

    const handleUpdateToken = async () => {
        const toastId = toast.loading(t("toast.updating", { entity: t("entity.token") }));

        if (!hasRole(Roles.Admin) && token.tokenType === "AUTOCHARGE") {
            toast.error(t("token.onlyAdminsAutocharge"), { id: toastId });
            return;
        }

        if (isMaxEnergyKwhInvalid(token.maxEnergyKwh)) {
            toast.error(t("token.maxEnergyInvalid"), { id: toastId });
            return;
        }
        const maxEnergyKwh = normalizeMaxEnergyKwh(token.maxEnergyKwh);
        try {
            const accessToken = await getAccessToken();

            const payload = {
                ...token,
                maxEnergyKwh,
                accumulatedEnergyKwh: undefined,
                tagToken: undefined,
                decryptedTagToken: undefined,
                tokenVehicleInfo: normalizeVehicleInfoForApi(token.tokenVehicleInfo),
            };
            const newToken = await updateToken(payload, accessToken);

            if (resetEnergyPending) {
                try {
                    await resetAccumulatedEnergy(token.idTagPublicId, accessToken);
                    newToken.accumulatedEnergyKwh = 0;
                    setResetEnergyPending(false);
                } catch {
                    setOriginalToken(newToken);
                    setToken(newToken);
                    toast.error(t("token.resetAccumulatedFailed"), { id: toastId });
                    return;
                }
            }

            setOriginalToken(newToken);
            setToken(newToken);
            setHasChanges(false);

            toast.success(t("toast.updated", { entity: t("entity.token") }), { id: toastId });
        } catch (error) {
            toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.token") }), { id: toastId });
        }
    };


    // ---------- ACCUMULATED ENERGY ----------
    // The reset is sent to the API on save, not on click.
    const resetAccumulatedEnergyOnSave = () => {
        setResetEnergyPending(true);
        setToken(prev => ({ ...prev, accumulatedEnergyKwh: 0 }));
    };


    // ---------- NAVIGATION ----------
    const handleBackClick = () => {
        const go = () => {
            if (customerPublicId) {
                navigate(`/customer/${customerPublicId}?tab=tokens`);
            } else if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate("/tokens");
            }
        };

        if (hasChanges || resetEnergyPending) {
            setConfirmState({
                isOpen: true,
                title: t("confirm.discard"),
                confirm: () => {
                    setConfirmState({ isOpen: false });
                    go();
                },
            });
        } else {
            go();
        }
    };

    if (loading) {
        return (
            <>
                <HeaderBar />
                <div className="flex flex-row w-full">
                    <Sidebar />
                    <main className="page flex items-center justify-center">
                        <LoadingSpinner text={t("common.loadingEntity", { name: t("form.tokenDetails") })} />
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />

                <main className="page">
                    <div className="page-content">
                        {/* Header */}
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={handleBackClick} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4">
                                {token?.name || token?.visualNumber}
                            </h2>
                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={handleBackClick}>
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges && !resetEnergyPending}
                                    onClick={handleUpdateToken}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        {/* Form container */}
                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="grid sm:grid-cols-1 gap-4">
                                <TokenForm
                                    token={token}
                                    handleInputChange={handleInputChange}
                                    customers={customers}
                                    isEditMode={true}
                                    onResetAccumulatedEnergy={resetAccumulatedEnergyOnSave}
                                />
                            </div>
                        </div>
                    </div>
                </main>

                <ConfirmationModal
                    isOpen={confirmState.isOpen}
                    title={confirmState.title}
                    onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
                    onConfirm={() => {
                        setConfirmState({ ...confirmState, isOpen: false });
                        confirmState.confirm();
                    }}
                />
            </div>
        </>
    );
};

export default EditTokenPage;
